import {matchPath, useLocation, useNavigation} from '@github-ui/react-router'
import type {Location} from '@github-ui/react-router'
import {announce} from '@github-ui/aria-live'
import type {FilterRef} from '@github-ui/filter'
import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {shouldDisplayBestMatchDefault} from '@github-ui/issue-query-free-text'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQueryClient} from '@github-ui/react-query'
import {useRepositoryFilterActions} from '@github-ui/issues-shared/repository-filter-actions'
import {FirstTimeContributionBannerDisplay} from '@github-ui/issues-shared/first-time-contribution-banner'
import {MoreResultsAvailableBanner} from '@github-ui/issues-shared/more-results-available-banner'
import {VALUES} from '@github-ui/issues-shared/values'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {RepoIssuesDashboardPageInfoSchema} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {AnalyticsWrapper as BaseAnalyticsWrapper} from '@github-ui/repo-issues-page-layout/AnalyticsWrapper'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {TanStackCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {ModifierKeys, useKeyPress} from '@github-ui/use-key-press'
import {reactFetch} from '@github-ui/verified-fetch'
import {Banner, Button, Stack} from '@primer/react'
import {startTransition, useCallback, useEffect, useMemo, useRef, useState, type RefObject} from 'react'

import {useIssuesIndexResults} from '../hooks/use-issues-index-results'
import {IssuesListSurface} from '../components/IssuesListSurface'
import {IssueRowsPreheater} from '../components/IssueRowsPreheater'
import {RepoIssuesBulkJobInfo} from '../components/RepoIssuesBulkJobInfo'
import {PinnedIssuesStrip} from '../components/pinned-issues/PinnedIssuesStrip'
import {createRepoRowNodeLoader, shouldFetchReactions} from '../hooks/repo-issues-row-node'
import {useRepoIssuesBulkActionState} from '../hooks/use-repo-issues-bulk-action-state'
import {useRepoIssuesSearchMetrics} from '../hooks/use-repo-issues-search-metrics'
import {useIssuesDashboardLiveUpdates} from '../hooks/use-issues-dashboard-live-updates'
import {useRowNodeOverlay} from '../hooks/use-row-node-overlay'
import {useIssuesListControls} from '../hooks/use-issues-list-controls'
import {isPullRequestsOnlyQuery} from '../helpers/is-explicit-issue-query'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'
import {useOptimisticRepoIssuesBulkUpdates} from '../helpers/repo-issues-bulk-optimistic-update'
import {
  issuesIndexQueryResolver,
  REPO_INDEX_CLOSED_QUERY,
  REPO_INDEX_DEFAULT_QUERY,
} from '../helpers/issues-index-query-resolver'
import {ssoOrgsInQuery} from '../helpers/sso-orgs-in-query'
import {IssuesIndexSearch} from './IssuesIndexSearch'
import {ISSUES_INDEX_PATHS, issuesIndexContentRoute, issuesLabelContentRoute} from './issues-index-content-route'
import {issuesLayoutRoute} from './issues-layout-route'
import {IssuesSidebarPreheater, useRenderSidebar} from './render-issues-sidebar'

const REFRESH_ERROR_ANNOUNCEMENT = 'Unable to refresh issues. Showing previously loaded results.'
const FOCUS_FILTER_KEYS = ['/']

function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

function isIssuesIndexPath(pathname: string): boolean {
  return Object.values(ISSUES_INDEX_PATHS).some(path => matchPath(path, pathname))
}

function focusSearchInput(searchInputRef: RefObject<HTMLInputElement | null>) {
  searchInputRef.current?.focus()
}

/**
 * Repo Issues#index content: a repo analog of `IssuesDashboardSurfaceContent` that
 * reuses the shared `IssuesList`, pagination, skeleton/blankslate, and a repo-scoped
 * `<Filter>` to render the mixed (issues + PRs) result list.
 */
export function IssuesIndexContent() {
  return (
    <TanStackCachePreheatContextProvider>
      <IssuesIndexContentWithPreheating />
    </TanStackCachePreheatContextProvider>
  )
}

function IssuesIndexContentWithPreheating() {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const filterRef = useRef<FilterRef>(null)
  const navigation = useNavigation()
  const location = useLocation()
  const isNavigating = Boolean(
    navigation.location &&
    isIssuesIndexPath(navigation.location.pathname) &&
    !isSameLocation(navigation.location, location),
  )
  const contentRoute = location.pathname.split('/')[3] === 'labels' ? issuesLabelContentRoute : issuesIndexContentRoute
  const {owner, repo} = useRouteParams(contentRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const renderSidebar = useRenderSidebar(owner, repo, layoutPayload)
  const searchParams = new URLSearchParams(location.search)
  const resolvedQuery = issuesIndexQueryResolver({pathname: location.pathname, searchParams})
  const showPinnedStrip =
    (resolvedQuery === REPO_INDEX_DEFAULT_QUERY || resolvedQuery === REPO_INDEX_CLOSED_QUERY) &&
    Number(searchParams.get('page') || 1) <= 1

  const keyboardShortcutsEnabled = useMemo(() => areCharacterKeyShortcutsEnabled(), [])
  const focusFilter = (event: KeyboardEvent) => {
    if (!keyboardShortcutsEnabled) return

    const input = searchInputRef.current
    const currentFilter = filterRef.current
    if (input && currentFilter) {
      currentFilter.setValueAndOpenSuggestions(input.value)
      event.preventDefault()
    }
  }

  useKeyPress(FOCUS_FILTER_KEYS, focusFilter, {[ModifierKeys.metaKey]: true})
  useKeyPress(FOCUS_FILTER_KEYS, focusFilter, {[ModifierKeys.ctrlKey]: true})

  return (
    <>
      {contentRoute === issuesIndexContentRoute ? <IssuesSidebarPreheater owner={owner} repo={repo} /> : null}
      <BaseAnalyticsWrapper category="Repository Issues Index" appName="issues">
        <RepoIssuesPageLayout renderSidebar={renderSidebar} initialExpanded={layoutPayload?.sidebarExpanded}>
          <Stack direction="vertical" gap="condensed">
            <IssuesIndexSearch
              inputRef={searchInputRef}
              filterRef={filterRef}
              beforeFilter={
                showPinnedStrip ? (
                  <PinnedIssuesStrip
                    owner={owner}
                    repo={repo}
                    pinnedIssues={layoutPayload?.pinnedIssues}
                    fallbackFocusRef={searchInputRef}
                  />
                ) : null
              }
            />
            <ErrorBoundary
              boundaryName="IssuesIndexContent"
              fallback={
                <div className="tmp-my-3">
                  <SharedDashboardBlankslate dashboardType="issues" variant="error" />
                </div>
              }
            >
              <IssuesIndexPageContent isNavigating={isNavigating} searchInputRef={searchInputRef} />
            </ErrorBoundary>
          </Stack>
        </RepoIssuesPageLayout>
      </BaseAnalyticsWrapper>
    </>
  )
}

function IssuesIndexPageContent({
  isNavigating,
  searchInputRef,
}: {
  isNavigating: boolean
  searchInputRef: RefObject<HTMLInputElement | null>
}) {
  const location = useLocation()
  const contentRoute = location.pathname.split('/')[3] === 'labels' ? issuesLabelContentRoute : issuesIndexContentRoute

  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const mainQuery = useSuspenseRouteQuery(contentRoute, 'mainQuery')
  const secondaryQuery = useRouteQuery(contentRoute, 'secondary')
  const queryClient = useQueryClient()
  const data = mainQuery.data
  const {owner, repo} = useRouteParams(contentRoute)
  const {currentPage, fetchResults, fetchedQuery, getPageHref, getQueryHref} = useIssuesIndexResults()
  const scopedRepository = useMemo(() => ({owner, name: repo}), [owner, repo])
  const fetchReactions = shouldFetchReactions(fetchedQuery)
  const rowNodeLoader = useMemo(
    () => createRepoRowNodeLoader(owner, repo, fetchReactions),
    [owner, repo, fetchReactions],
  )
  const rowNodeQueryScope = `${owner}/${repo}:fetch-reactions=${fetchReactions}`

  const applySectionFilter = useCallback(
    (query: string) => {
      startTransition(() => {
        fetchResults({q: query})
      })
    },
    [fetchResults],
  )
  const filterActions = useRepositoryFilterActions({
    repo: scopedRepository,
    query: fetchedQuery,
    applySectionFilter,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
  })

  const secondaryData = secondaryQuery.data?.payload?.issuesIndexSecondaryData
  const secondaryResults = secondaryData?.results
  const listRows = useMemo(
    () => mergeDeferredIssues(repoIssuesToListItems(data.results ?? []), secondaryResults ?? []),
    [data.results, secondaryResults],
  )
  // Per-row live-update overlay: an Alive event refreshes just that row via the
  // repo `_rows` endpoint instead of refetching the list.
  const rowNodeIssues = useRowNodeOverlay({
    rows: listRows,
    listUpdatedAt: mainQuery.dataUpdatedAt,
    loadRowNode: rowNodeLoader.loadRowNode,
    queryKeyScope: rowNodeQueryScope,
  })
  const issues = useOptimisticRepoIssuesBulkUpdates(rowNodeIssues)
  const liveUpdates = useIssuesDashboardLiveUpdates(listRows, rowNodeQueryScope)

  // Labelling only. github/github#423765 orders a relevance search newest-first when the backend
  // has no text left to rank after extracting qualifiers, so the label has to follow the same
  // rule. The request query is unaffected and may still carry `sort:relevance-desc`.
  const defaultSortingKey = shouldDisplayBestMatchDefault(layoutPayload?.bestMatchSortEnabled, fetchedQuery)
    ? 'relevance'
    : 'created'
  const pageInfo = create(RepoIssuesDashboardPageInfoSchema, {
    currentPage: data.pageInfo?.currentPage ?? 1,
    totalPages: data.pageInfo?.totalPages ?? 1,
    totalCount: data.pageInfo?.totalCount ?? 0,
    openCount: secondaryData?.pageInfo?.openCount,
    closedCount: secondaryData?.pageInfo?.closedCount,
  })
  const repositoryId = layoutPayload && 'repositoryId' in layoutPayload ? layoutPayload.repositoryId : ''
  const viewerCanBulkUpdate = layoutPayload && 'canBulkUpdate' in layoutPayload && layoutPayload.canBulkUpdate === true
  const refreshIssues = async () => {
    // A refetch otherwise reuses an initial deferred request that started before the write.
    if (secondaryQuery.queryKey) {
      await queryClient.cancelQueries({queryKey: secondaryQuery.queryKey, exact: true})
    }
    const [main, secondary] = await Promise.all([mainQuery.refetch(), secondaryQuery.refetch()])
    return main.isError ? main : secondary
  }
  const {
    bulkJobId,
    clearBulkJobId,
    selection,
    bulkActionError,
    clearBulkActionError,
    onBulkJobCompleted,
    onBulkJobError,
  } = useRepoIssuesBulkActionState({
    owner,
    repo,
    currentPage,
    query: fetchedQuery,
    repositoryId,
    currentUserId: layoutPayload?.currentUser?.id ?? '',
    viewerCanBulkUpdate,
    totalCount: pageInfo.totalCount,
    issues,
    hasProjectsEnabled: layoutPayload?.hasProjectsEnabled ?? false,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
    refreshIssues,
  })
  const {trackRowSelection, trackIssueNavigation} = useRepoIssuesSearchMetrics(
    new URLSearchParams(location.search).get('q'),
    data.issueSearchType,
    bulkJobId,
  )

  const onlyPrs = isPullRequestsOnlyQuery(fetchedQuery)
  const itemsLabel = onlyPrs ? 'pull requests' : 'issues'
  const maxItems = VALUES.maxIssuesListItems(onlyPrs, Boolean(layoutPayload?.currentUser))
  const showMoreResultsAvailable = Boolean(
    pageInfo && pageInfo.currentPage === pageInfo.totalPages && pageInfo.totalCount > maxItems,
  )
  const cappedResultsAnnouncement = showMoreResultsAvailable
    ? `Showing the first ${maxItems} ${itemsLabel}. To view additional ${itemsLabel}, please refine your search.`
    : undefined
  // Persisted density + screen-reader announcements, shared with the dashboard. Density is
  // server-seeded from the layout payload so the list renders at the persisted density on first paint.
  const {compactMode, onCompactModeChange, emptyListState} = useIssuesListControls({
    initialCompactMode: layoutPayload?.compactMode ?? false,
    compactModePreferenceKey: 'dashboard_surface_compact_mode',
    isFetching: isNavigating,
    currentPage,
    fetchedQuery,
    defaultSortingKey,
    totalCount: pageInfo.totalCount,
    totalPages: pageInfo.totalPages,
    errorMessage: data.error || undefined,
    additionalAnnouncement: cappedResultsAnnouncement,
  })

  const error = data.error || null
  const orgsRequiringSso = useMemo(
    () => ssoOrgsInQuery(fetchedQuery, layoutPayload?.ssoOrganizationLogins ?? []),
    [fetchedQuery, layoutPayload?.ssoOrganizationLogins],
  )
  useEffect(() => {
    if (mainQuery.isRefetchError) announce(REFRESH_ERROR_ANNOUNCEMENT)
  }, [mainQuery.isRefetchError])

  if (error) {
    return (
      <div className="tmp-my-3">
        <SharedDashboardBlankslate dashboardType="issues" variant="error" />
      </div>
    )
  }

  return (
    <>
      {mainQuery.isRefetchError && (
        <Banner
          variant="warning"
          title="Unable to refresh issues."
          description="Showing previously loaded results."
          primaryAction={
            <Button
              onClick={async () => {
                const result = await mainQuery.refetch()
                if (result.isRefetchError) {
                  announce(REFRESH_ERROR_ANNOUNCEMENT)
                } else if (result.isSuccess) {
                  focusSearchInput(searchInputRef)
                }
              }}
            >
              Retry
            </Button>
          }
        />
      )}
      <RepoIssuesBulkJobInfo
        jobId={bulkJobId}
        enabled
        clearJobId={clearBulkJobId}
        refreshIssues={refreshIssues}
        onCompleted={onBulkJobCompleted}
        onError={onBulkJobError}
      />
      {bulkActionError ? (
        <Banner className="mt-3" variant="critical" title={bulkActionError} onDismiss={clearBulkActionError} />
      ) : null}
      {layoutPayload?.firstTimeContributionBanner && (
        <FirstTimeContributionBanner
          banner={layoutPayload.firstTimeContributionBanner}
          searchInputRef={searchInputRef}
        />
      )}
      <SingleSignOnBanner protectedOrgs={orgsRequiringSso} />
      {!isNavigating && (
        <IssueRowsPreheater
          rows={issues}
          listIdentity={JSON.stringify([location.pathname, fetchedQuery, currentPage])}
          source="IssuesIndex"
        />
      )}
      <IssuesListSurface
        response={{...data, pageInfo}}
        routeAdapter={{
          scopeIdentity: `repository:${owner}/${repo}`,
          query: fetchedQuery,
          page: currentPage,
          fetch: ({query, page}) => fetchResults({q: query, page}),
          getPageHref,
          getQueryHref,
        }}
        filterActions={filterActions}
        defaultSortingKey={defaultSortingKey}
        separateAgentAssignees
        isLoading={isNavigating}
        issuesOverride={issues}
        beforeList={liveUpdates}
        compactMode={compactMode}
        onCompactModeChange={onCompactModeChange}
        onSelectRow={trackRowSelection}
        onIssueNavigate={trackIssueNavigation}
        emptyListStateOverride={emptyListState}
        selection={selection}
      />
      {showMoreResultsAvailable && <MoreResultsAvailableBanner itemsLabel={itemsLabel} maxItems={maxItems} />}
    </>
  )
}

function FirstTimeContributionBanner({
  banner,
  searchInputRef,
}: {
  banner: NonNullable<
    NonNullable<
      ReturnType<typeof useRouteQuery<typeof issuesLayoutRoute, 'mainQuery'>>['data']
    >['firstTimeContributionBanner']
  >
  searchInputRef: RefObject<HTMLInputElement | null>
}) {
  const [dismissed, setDismissed] = useState(false)

  const dismiss = useCallback(
    async (url: string, method: 'DELETE' | 'POST') => {
      try {
        const response = await reactFetch(url, {method})
        if (!response.ok) return

        searchInputRef.current?.focus()
        setDismissed(true)
        announce('First-time contribution banner dismissed')
      } catch {
        // Keep the banner visible so the viewer can retry.
      }
    },
    [searchInputRef],
  )

  if (dismissed) return null

  return (
    <FirstTimeContributionBannerDisplay
      repoNameWithOwner={banner.repoNameWithOwner}
      contributingGuidelinesUrl={banner.contributingGuidelinesUrl}
      hasGoodFirstIssueIssues={banner.hasGoodFirstIssueIssues}
      contributeUrl={banner.contributeUrl}
      dismissForThisRepo={() => void dismiss(banner.dismissForRepoUrl, 'DELETE')}
      dismissForAllRepos={() => void dismiss(banner.dismissForAllUrl, 'POST')}
    />
  )
}
